import React, { useEffect, useState, useRef } from 'react'
import {
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  DialogContentText,
  FormControlLabel,
  Checkbox,
  Typography,
  useTheme
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReCAPTCHA from 'react-google-recaptcha'

import { EmailType, ErrorReportEmailPayload } from '../types/apiTypes'
import { sendEmail } from '../api/email_api'
import { AppContext } from '../contexts/AppProvider'
import { isNumeric } from '../utils/mathsUtils'
import CharacterCounter from './CharLimitMessage'
import useIsMobile from '../hooks/useIsMobile'
import ClosableModalTitle from './ClosableModalTitle'
import { fetchEnvironmentVar } from '../utils/miscUtils'

interface ErrorReportDialogProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const ErrorReportDialog: React.FC<ErrorReportDialogProps> = ({ open, setOpen }) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const { appState } = React.useContext(AppContext)
  const { currentMeasure } = appState
  const { fromNumber, toNumber, fromUnit, toUnit } = appState.tabs[currentMeasure.codeName]

  const [dialogState, setDialogState] = useState<Record<string, any>>({
    senderEmail: '',
    message: '',
    isErrorWithConversion: false,
    currentConversionMessage: null,
    isRecaptchaPassed: false
  })
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const createCurrentConversionMessage = (): string => {
    return `${currentMeasure.displayName}: ${fromNumber} ${fromUnit.displayName} is equal to ${toNumber} ${toUnit.displayName}`
  }

  useEffect(() => {
    if (isNumeric(fromNumber)) {
      setDialogState((prevState) => ({
        ...prevState,
        currentConversionMessage: createCurrentConversionMessage()
      }))
    } else {
      setDialogState((prevState) => ({
        ...prevState,
        isErrorWithConversion: false,
        currentConversionMessage: null
      }))
    }
  }, [fromNumber, toNumber, fromUnit, toUnit])


  useEffect(() => {
    // This runs when the dialog closes
    if (!open && dialogState.isRecaptchaPassed) {
      setDialogState(prevState => ({
        ...prevState,
        isRecaptchaPassed: false
      }))

      // Reset the actual reCAPTCHA widget
      if (recaptchaRef.current) {
        recaptchaRef.current.reset()
      }
    }
  }, [open, dialogState.isRecaptchaPassed])

  const errorMessage = 'There was an error sending your request. Please try again later.'
  const inputCharLimit = 500

  const handleDialogFieldChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.value.length > inputCharLimit) {
      return
    }

    const { id, value } = event.target
    setDialogState((prevState) => ({
      ...prevState,
      [id]: value
    }))
  }

  const sendMessage = useMutation({
    mutationFn: async (payload: ErrorReportEmailPayload) => {
      return await sendEmail(payload)
    },
    onSuccess: () => {
      setDialogState({
        senderEmail: '',
        message: '',
        isErrorWithConversion: false,
        currentConversionMessage: createCurrentConversionMessage(),
        isRecaptchaPassed: false
      })
      toast.info('Error report sent successfully! Thank you for your feedback.')
    },
    onError: (error) => {
      console.error('Error submitting error report:', error)
      toast.error(errorMessage)
    }
  })

  const preparePayload = (): ErrorReportEmailPayload => ({
    email_request: {
      email_type: EmailType.ErrorReport,
      ...(dialogState.senderEmail && { sender_email: dialogState.senderEmail }),
      message: dialogState.message,
      is_error_with_conversion: dialogState.isErrorWithConversion,
      ...(dialogState.isErrorWithConversion &&
        { current_conversion: dialogState.currentConversionMessage })
    }
  })

  const handleIsErrorWithConversionCheckboxClick = (): void => {
    setDialogState((prevState) => ({
      ...prevState,
      isErrorWithConversion: !prevState.isErrorWithConversion
    }))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    sendMessage.mutate(preparePayload())
  }

  const handleClose = (): void => {
    setOpen(false)
    setTimeout(() => sendMessage.reset(), 1000)
  }

  const isSubmissionInvalid = (state: Record<string, string>): boolean => {
    return !state.message || !state.isRecaptchaPassed
  }

  const handleOnChangeReCAPTCHA = (): void => {
    const reValue: string = recaptchaRef.current.getValue()

    setDialogState(prevState => ({
      ...prevState,
      isRecaptchaPassed: !!reValue
    }))
  }


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: '100%', sm: '500px' }
        }
      }}
    >
      <Box sx={{ padding: { xs: 1, sm: 4 }, width: '100%' }}>
        {!sendMessage.isPending && !sendMessage.isSuccess && (
          <form onSubmit={handleSubmit}>
            <ClosableModalTitle title="Report Errors" onClose={handleClose} marginLeft='-6px' />
            {sendMessage.isError &&
              <DialogContentText sx={{ color: theme.palette.custom.attentionRed }}>
                {errorMessage}
              </DialogContentText>
            }
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Checkbox for "Is error with current conversion?" */}
                <FormControlLabel
                  control={
                    <Checkbox
                      id="isErrorWithConversion"
                      checked={dialogState.isErrorWithConversion || false}
                      onChange={handleIsErrorWithConversionCheckboxClick}
                      disabled={!isNumeric(fromNumber)}
                    />
                  }
                  label="Is error with current conversion?"
                />

                {/* Show current conversion details only if the checkbox is checked */}
                {dialogState.isErrorWithConversion && (
                  <Typography variant="body2" color="textSecondary">
                    {dialogState.currentConversionMessage}
                  </Typography>
                )}

                {/* Sender Email */}
                <TextField
                  id="senderEmail"
                  label="Your email"
                  type="email"
                  value={dialogState.senderEmail}
                  onChange={handleDialogFieldChange}
                  variant="outlined"
                  fullWidth
                />

                {/* Message */}
                <TextField
                  id="message"
                  label="Please, describe the issue"
                  type="text"
                  value={dialogState.message}
                  onChange={handleDialogFieldChange}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  required
                />
                <CharacterCounter text={dialogState.message} charLimit={inputCharLimit} />
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="normal"
                  sitekey={fetchEnvironmentVar('REACT_APP_GOOGLE_SITE_KEY')}
                  onChange={handleOnChangeReCAPTCHA}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ padding: 3 }}>
              <Button onClick={handleClose} color="secondary">
                Cancel
              </Button>
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: theme.palette.custom.beautifulGradient
                }}
                type="submit"
                disabled={isSubmissionInvalid(dialogState)}
              >
                Submit
                <FontAwesomeIcon icon={faEnvelope} style={{ marginLeft: '8px', fontSize: '1rem' }} />
              </Button>
            </DialogActions>
          </form>
        )}
        {sendMessage.isPending && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {sendMessage.isSuccess && (
          <DialogContentText>
            Error report sent successfully! Thank you for your feedback.
          </DialogContentText>
        )}
        {sendMessage.isError &&
          <DialogContentText sx={{ color: theme.palette.custom.attentionRed }}>
            {errorMessage}
          </DialogContentText>
        }
      </Box>
    </Dialog>
  )
}

export default ErrorReportDialog
